import { useCallback, useEffect, useRef, useState } from 'react';

interface InstantShapeToggleHandlers {
  /** Runs once when instant-shape mode is switched from off to on. */
  onEnabled?: () => void;
  /** Runs once when instant-shape mode is switched from on to off. */
  onDisabled?: () => void;
}

interface InstantShapeAutoTrigger {
  /**
   * True for the render(s) after the user enables instant-shape mode, until a
   * caller resets it. Consumers should only auto-trigger drawing for the first
   * shape field (`shouldAutoTrigger && isFirstShapeItem`) so multi-area reports
   * don't register competing draw-accept handlers on the shared draw context.
   */
  shouldAutoTrigger: boolean;
  /** Clears `shouldAutoTrigger` once the draw has been kicked off. */
  resetAutoTrigger: () => void;
}

/**
 * Owns the "user just enabled instant-shape mode" state machine that drives the
 * auto-trigger of the in-dialog drawing UI. Extracted so the create wizard
 * (ShapeStep) and the clone form (ShapeFormField) share one implementation -
 * the previous copy-paste of this wiring is what let the first-field guard
 * drift between the two (CLASSIC-10044).
 *
 * Handlers are read through a ref so callers can pass inline closures without
 * destabilising the transition effect.
 */
export const useInstantShapeAutoTrigger = (
  instantShape: boolean,
  handlers?: InstantShapeToggleHandlers,
): InstantShapeAutoTrigger => {
  const previousInstantShapeRef = useRef(instantShape);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const [shouldAutoTrigger, setShouldAutoTrigger] = useState(false);

  useEffect(() => {
    const wasEnabled = previousInstantShapeRef.current;
    if (!wasEnabled && instantShape) {
      setShouldAutoTrigger(true);
      handlersRef.current?.onEnabled?.();
    } else if (wasEnabled && !instantShape) {
      handlersRef.current?.onDisabled?.();
    }
    previousInstantShapeRef.current = instantShape;
  }, [instantShape]);

  const resetAutoTrigger = useCallback(() => setShouldAutoTrigger(false), []);

  return { shouldAutoTrigger, resetAutoTrigger };
};
